"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner, FormFeedback } from "reactstrap"
import { ExhibitionStandCategoryService, AllStandCategoryService } from "../../../Service/ExhibitionRelatedService"
import  { ExhibitionStandCategory } from "./types"
import { toast } from "react-toastify"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  initialData: ExhibitionStandCategory | null
  exhibitionId: number
}

const ExhibitionStandCategoryModal: React.FC<Props> = ({ isOpen, toggle, onSuccess, initialData, exhibitionId }) => {
  const [form, setForm] = useState<Omit<ExhibitionStandCategory, "id">>({
    exhibition: exhibitionId,
    category: 0,
  })

  const [categories, setCategories] = useState<ExhibitionStandCategory[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  // Fetch available stand categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true)
        const categories = await AllStandCategoryService.getAll()
        setCategories(categories) 
      } catch (err) {
        console.error("Failed to fetch categories:", err)
        toast.error("Failed to load categories")
      } finally {
        setLoadingCategories(false)
      }
    }

    if (isOpen) {
      fetchCategories()
    }
  }, [isOpen])

  useEffect(() => {
    if (initialData) {
      setForm({
        exhibition: initialData.exhibition,
        category: initialData.category,
      })
    } else {
      setForm({
        exhibition: exhibitionId,
        category: 0,
      })
    }
  }, [initialData, exhibitionId])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setForm({
      ...form,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : type === "number" ? Number(value) : value,
    })

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}
    if (!form.category || form.category === 0) newErrors.category = "Category is required"
    return newErrors
  }

  const handleSubmit = async () => {
    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    try {
      setLoading(true)
      if (initialData) {
        await ExhibitionStandCategoryService.update(initialData.id, form)
        toast.success("Exhibition stand category updated successfully!")
      } else {
        await ExhibitionStandCategoryService.create(form)
        toast.success("Exhibition stand category created successfully!")
      }
      onSuccess()
      toggle()
    } catch (error) {
      toast.error("Something went wrong. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>{initialData ? "Edit" : "Add"} Exhibition Stand Category</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="category">Stand Category</Label>
          <Input
            type="select"
            name="category"
            value={form.category}
            onChange={handleChange}
            invalid={!!errors.category}
            disabled={loadingCategories}
          >
            <option value={0}>-- Select Category --</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Input>
          <FormFeedback>{errors.category}</FormFeedback>
          {loadingCategories && (
            <small className="text-muted">
              <Spinner size="sm" className="me-1" />
              Loading categories...
            </small>
          )}
        </FormGroup>

        <div className="text-end mt-4">
          <Button color="primary" onClick={handleSubmit} disabled={loading || loadingCategories}>
            {loading ? <Spinner size="sm" /> : initialData ? "Update" : "Create"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ExhibitionStandCategoryModal
